import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/24/outline';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { PRICING_INFO, BillingInterval } from '../config/stripeConfig';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>('monthly');
  const [pricingImage, setPricingImage] = useState<string>('/phone-mockup.png');
  const [imageLoading, setImageLoading] = useState(true);

  // Get current pricing based on selected interval
  const currentPricing = PRICING_INFO[billingInterval];

  // Fetch pricing image from Firestore
  useEffect(() => {
    const fetchPricingImage = async () => {
      try {
        const docRef = doc(db, 'landingPageContent', 'pricing');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.leftImage) {
            setPricingImage(data.leftImage);
          }
        }
      } catch (error) {
        console.error('Error fetching pricing image:', error);
        // Keep using default fallback image
      } finally {
        setImageLoading(false);
      }
    };

    fetchPricingImage();
  }, []);

  const handleContinueToPayment = () => {
    // Navigate to checkout page with selected plan
    navigate(`/checkout?plan=${billingInterval}`);
  };

  const features = [
    'Get Access to our VoIP Caller',
    'Call histories on all of the calls that comes through Unveil App',
    'Call history and transparent call details summary',
    'Caller history retention for up to 30 days',
    'Call analytics dashboard',
    'Priority customer support',
    'Up to date security updates for our app',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center">
          {/* Logo - clickable */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 hover:opacity-80 transition"
          >
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
              />
            </svg>
            <span className="text-2xl font-bold text-gray-900">Unveil</span>
          </button>

          <button
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-gray-900"
          >
            ← Back to home
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Our pricing</h2>
          <p className="text-xl text-gray-600 mb-8">
            Simple, transparent pricing for everyone
          </p>

          {/* Billing Toggle */}
          <div className="flex justify-center">
            <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setBillingInterval('monthly')}
                className={`px-6 py-2 rounded-md font-medium transition-all ${
                  billingInterval === 'monthly'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval('yearly')}
                className={`px-6 py-2 rounded-md font-medium transition-all relative ${
                  billingInterval === 'yearly'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Yearly
                {/* Savings badge */}
                <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                  Save {PRICING_INFO.yearly.savingsPercentage}
                </span>
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Left Side - Phone Mockup */}
          <div className="flex items-center justify-center">
            <div className="relative">
              {imageLoading ? (
                <div className="w-full max-w-md h-96 flex items-center justify-center bg-gray-100 rounded-lg">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <img
                  src={pricingImage}
                  alt="Unveil App"
                  className="w-full max-w-md"
                  onError={(e) => {
                    // Fallback to default image if loading fails
                    const target = e.target as HTMLImageElement;
                    target.src = '/phone-mockup.png';
                  }}
                />
              )}
            </div>
          </div>

          {/* Right Side - Pricing */}
          <div className="flex flex-col justify-center">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                What We Offer
              </h3>

              {/* Features List */}
              <ul className="space-y-3 mb-8">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Price Display */}
              <div className="bg-blue-50 rounded-lg p-6 mb-8">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">
                    {currentPricing.displayPeriod} Subscription
                  </p>
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-blue-600">
                      {currentPricing.displayPrice}
                    </span>
                    <span className="text-xl text-gray-600 ml-2">
                      {currentPricing.displayInterval}
                    </span>
                  </div>

                  {/* Show additional info based on billing interval */}
                  {billingInterval === 'yearly' ? (
                    <div className="mt-3 space-y-1">
                      <p className="text-sm font-medium text-green-600">
                        {PRICING_INFO.yearly.savings}
                      </p>
                      <p className="text-xs text-gray-600">
                        Only {PRICING_INFO.yearly.monthlyEquivalent}!
                      </p>
                    </div>
                  ) : (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500">
                        {PRICING_INFO.monthly.totalAnnual} billed monthly
                      </p>
                    </div>
                  )}

                  <p className="text-sm text-gray-600 mt-3">
                    No commitment, cancel anytime.
                  </p>
                </div>
              </div>

              {/* Continue Button */}
              <button
                onClick={handleContinueToPayment}
                className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-blue-700 transition text-lg"
              >
                Continue to Payment
              </button>

              <p className="text-center text-sm text-gray-500 mt-4">
                Secure payment powered by Stripe
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
